import {useState} from 'react';
import {EvReactModal} from '../../modal/EvReactModal';
import {
  ArticleProductionCopyForm,
  ArticleProductionStepCopyModalState,
} from './article-steps-copy.models';
import {ArticleStepsSearchList} from './article-steps-search-list';
import {EvButton} from '../../form/EvButton';
import {Article} from '../../../envisia/article/models/article.models';
import {useArticleCountBa} from './use-article-count-ba';
import {useArticleStepsCopyMutation} from './use-article-steps-copy-mutation';
import {useForm} from '@tanstack/react-form';
import {EnvisiaObject} from '../../../common/wrapper.models';
import {UseQueryResult} from '@tanstack/react-query';
import {EvLabel} from '../../components/labels/ev-label';
import {appHref} from '../../../helper/app-href';

function articleSelectedValidation(value: string | null): string | undefined {
  return !value ? 'Kein Artikel zum Kopieren ausgewählt' : undefined;
}

function ignoreExistingBaAcceptedValidation(
  value: boolean,
  useBa: UseQueryResult<EnvisiaObject<number>>,
): string | undefined {
  return (!value && ((useBa.data?.object ?? 0) > 0)) ? 'Fertigungsliste neu erstellen anhaken' : undefined;
}

interface Props {
  article: Article;
  updateList: () => void;
}

export function ArticleStepsCopyButton(props: Props) {
  const [state, setState] = useState<ArticleProductionStepCopyModalState>({
    modalState: 'closed',
  });

  const toggleModal: (modalState: 'open' | 'closed') => void = (modalState) => {
    setState({...state, modalState: modalState});
  };

  const useBa = useArticleCountBa(props.article.oa_nr);
  const useMutation = useArticleStepsCopyMutation();

  const form = useForm({
    defaultValues: {
      targetOaNr: props.article.oa_nr,
      originOaNr: null,
      ignoreExistingBa: false,
    } as ArticleProductionCopyForm,
    onSubmit: async ({formApi}) => {
      await useMutation.mutateAsync(formApi.state.values);
      formApi.reset();
      props.updateList();
      toggleModal('closed');
    },
  });

  return <>
    <EvButton type="button" size="sm" onClick={() => toggleModal('open')}>
      Schritte von anderem Artikel Kopieren
    </EvButton>

    <EvReactModal contentLabel="Artikel Produktionsschritte kopieren"
                  showModal={state.modalState === 'open'}
                  modalClosed={() => toggleModal('closed')}>
      <div className="p-0 pr-4 pl-4">
        <div>
          Alle Fertigungsschritte werden aus der Liste entfernt und mit den Fertigungsschritten
          aus dem ausgewählten Artikel <strong>ersetzt</strong>.
        </div>
      </div>
      <div className="p-0 pr-4 pl-4">
        <form.Field name="originOaNr"
                    validators={{
                      onMount: ({value}) => articleSelectedValidation(value),
                      onChange: ({value}) => articleSelectedValidation(value),
                    }}>
          {(field) =>
            <ArticleStepsSearchList selected={field.state.value}
                                    selectionChanged={oaNr =>
                                      oaNr === field.state.value ?
                                        field.handleChange(null) :
                                        field.handleChange(oaNr)}/>
          }
        </form.Field>
      </div>
      <form onSubmit={async s => {
        s.preventDefault();
        await form.handleSubmit(s);
      }}>
        <div className="p-0 pr-4 pl-4">
          {(useBa.isSuccess && ((useBa.data?.object ?? 0) === 0)) ? null :
            <div className="alert alert-danger">
              <div className="row">
                <div className="col-sm-2">
                  <form.Field validators={{
                    onMount: ({value}) =>
                      ignoreExistingBaAcceptedValidation(value, useBa),
                    onChange: ({value}) =>
                      ignoreExistingBaAcceptedValidation(value, useBa),
                  }}
                              name="ignoreExistingBa">
                    {(field) =>
                      <input id="ignore_existing_ba"
                             type="checkbox"
                             checked={field.state.value}
                             onChange={(e) => field.handleChange(e.target.checked)}
                             className="p-0 m-0! w-full h-9 bg-white"/>
                    }
                  </form.Field>
                </div>
                <div className="col-sm-10">
                  <label htmlFor="ignore_existing_ba">
                    <p><strong>Achtung:</strong></p>
                    <p>Es sind Betriebsaufträge mit dieser Fertigungsliste in Produktion.</p>
                    <p>
                      Bei der Neuerstellung der Liste geht die Verbindung zu den aktiven Betriebsaufträgen verloren.
                    </p>
                    <p>
                      Somit können keine Info- oder Sperrtexte für aktive Betriebsaufträge gesetzt werden.
                    </p>
                    <p>
                      <strong>Fertigungsliste wirklich neu erstellen?</strong>
                    </p>
                  </label>
                </div>
              </div>
              <a href={appHref(`/workflow/business/order/?status=1&sds_nr=${props.article.sds_nr}`)} target="_blank">
                Liste mit Betriebsaufträgen in Produktion mit diesem Artikel öffnen
              </a>
            </div>
          }
        </div>

        {!useMutation.isError ? null :
          <div className="p-0 pr-4 pl-4">
            <EvLabel labeltype="danger">Konnte Schritte nicht kopieren</EvLabel>
          </div>
        }

        <div className="modal-footer">
          <EvButton className="float-left"
                    onClick={() => toggleModal('closed')}>
            Abbrechen
          </EvButton>
          <form.Subscribe selector={s => [s.canSubmit, s.isSubmitting]}>
            {([canSubmit, isSubmitting]) =>
              <EvButton type="submit"
                        variant="success"
                        className="float-right"
                        disabled={!canSubmit || isSubmitting}>
                Übernehmen
              </EvButton>
            }
          </form.Subscribe>
        </div>
      </form>
    </EvReactModal>
  </>;
}
